/**
 * Product Click Tracker для Google Analytics Enhanced Ecommerce
 * Відстежує кліки по картках товарів та відправляє подію select_item
 * Тепер дозволяє постійні кліки без блокування дублювання
 */

class ProductClickTracker {
    constructor() {
        this.listContext = null;
        // Глобальний реєстр для запобігання дублюванню
        if (!window.__globalClickTracker) {
            window.__globalClickTracker = {
                lastSentAt: new Map(),
                currentPage: null
            };
        }
        this.globalTracker = window.__globalClickTracker;
    }
    /**
     * Встановлює контекст списку товарів
     * @param {string} listName - Назва списку товарів
     * @param {string} listId - ID списку товарів
     */
    setListContext(listName, listId) {
        // Перевіряємо, чи змінився контекст
        const newContext = {
            name: 'Product Card',
            id: 'product-card'
        };
        
        const contextChanged = !this.listContext || 
            this.listContext.name !== newContext.name || 
            this.listContext.id !== newContext.id;
        
        this.listContext = newContext;
        
        // Очищаємо стан трекера тільки якщо контекст змінився
        if (contextChanged) {
            this.clearGlobalTracker();
        }
    }
    /**
     * Форматує дані товару для Google Analytics
     * @param {Object} productData - Дані товару
     * @returns {Object}
     */
    formatProductData(productData) {
        console.log('🔄 Форматування даних товару...');
        
        const price = this.extractPrice(productData);
        console.log('💰 Витягнута ціна:', price);
        
        // Визначаємо категорію
        let finalCategory = '';
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                finalCategory = sessionStorage.getItem('ga_last_item_category') || '';
                console.log('📂 Категорія з sessionStorage:', finalCategory);
            }
        } catch (e) { /* no-op */ }
        
        if (!finalCategory && productData.category) {
            finalCategory = productData.category.name || productData.category.title || '';
            console.log('📂 Категорія з productData.category:', finalCategory);
        }
        
        if (!finalCategory) {
            finalCategory = 'Невизначена категорія';
        }
        
        // Визначаємо бренд
        let finalBrand = productData.productBrand?.title || '';
        console.log('🏷️ Бренд з productBrand:', finalBrand);
        
        if (!finalBrand && productData.title) {
            finalBrand = this.extractBrandFromTitle(productData.title);
            console.log('🏷️ Бренд з заголовка:', finalBrand);
        }
        
        if (!finalBrand) {
            finalBrand = 'Невизначений бренд';
        }
        
        const productId = this.extractProductId(productData);
        const formattedData = {
            item_name: productData.title || '',
            item_id: this.normalizeId(productId),
            price: price,
            currency: 'UAH',
            item_brand: finalBrand,
            item_category: finalCategory,
        };
        
        console.log('✅ Сформовані дані для GA:', formattedData);
        return formattedData;
    }
    /**
     * Витягує бренд з заголовка товару
     * @param {string} title - Заголовок товару
     * @returns {string}
     */
    extractBrandFromTitle(title) {
        if (!title) return '';
        
        const t = title.trim();
            if (!t) return '';
            
        // Пріоритет 1: Бренд до коми
            const commaIdx = t.indexOf(',');
            if (commaIdx > 0) {
                const beforeComma = t.slice(0, commaIdx).trim();
                if (beforeComma.length >= 2 && beforeComma.length <= 60) {
                    return beforeComma;
                }
            }
            
        // Пріоритет 2: Відомі бренди
            const knownBrands = ['BauGlas', 'RODECA', 'BAUGLAS'];
            for (const brand of knownBrands) {
            const escapedBrand = brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const brandWithModifier = t.match(new RegExp(`\\b${escapedBrand}\\s+([A-Z]{2,}(?:\\s+[A-Za-z]+)*)\\b`, 'i'));
                if (brandWithModifier && brandWithModifier[0]) {
                    return brandWithModifier[0];
                }
                
            const brandMatch = t.match(new RegExp(`\\b${escapedBrand}\\b`, 'i'));
                if (brandMatch && brandMatch[0]) {
                    return brandMatch[0];
                }
            }
            
        // Пріоритет 3: CamelCase
            const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+(?:\s+[A-Z]+)*\b/);
            if (camelMatch && camelMatch[0]) {
                return camelMatch[0];
            }
            
        // Пріоритет 4: Слова з великих літер
            const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
            if (capsMatch && capsMatch[0]) {
                return capsMatch[0];
            }
            
        // Пріоритет 5: Перше слово з 3+ літер
            const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
            if (firstWord && firstWord[0]) {
                return firstWord[0];
            }
            
            return '';
    }
    /**
     * Витягує ціну з даних товару
     * @param {Object} productData - Дані товару
     * @returns {number}
     */
    extractPrice(productData) {
        if (productData.productPrice) {
            return parseFloat(productData.productPrice.price?.price || 0);
        }
        return 0;
    }
    /**
     * Витягує правильний ID товару з пріоритетом product_price_id
     * @param {Object} productData - Дані товару
     * @returns {any} ID товару
     */
    extractProductId(productData) {
        if (!productData) {
            return '';
        }
        
        // Пріоритет 1: productPrice.price.productPriceId (camelCase - основне місце з API)
        const priceProductPriceId = productData.productPrice?.price?.productPriceId;
        if (priceProductPriceId !== null && priceProductPriceId !== undefined) {
            return priceProductPriceId;
        }
        
        // Пріоритет 2: productPrice.priceOld.productPriceId (camelCase - якщо є знижка)
        const priceOldProductPriceId = productData.productPrice?.priceOld?.productPriceId;
        if (priceOldProductPriceId !== null && priceOldProductPriceId !== undefined) {
            return priceOldProductPriceId;
        }
        
        // Пріоритет 3: productPrice.price.product_price_id (snake_case fallback)
        const snakeCasePriceId = productData.productPrice?.price?.product_price_id;
        if (snakeCasePriceId !== null && snakeCasePriceId !== undefined) {
            return snakeCasePriceId;
        }
        
        // Пріоритет 4: productPrice.price_old.product_price_id (snake_case fallback)
        const snakeCasePriceOldId = productData.productPrice?.price_old?.product_price_id;
        if (snakeCasePriceOldId !== null && snakeCasePriceOldId !== undefined) {
            return snakeCasePriceOldId;
        }
        
        // Пріоритет 5: product_price_id на верхньому рівні об'єкта (fallback)
        const topLevelProductPriceId = productData.product_price_id;
        if (topLevelProductPriceId !== null && topLevelProductPriceId !== undefined) {
            return topLevelProductPriceId;
        }
        
        // Якщо product_price_id не знайдено - повертаємо порожній рядок (порожня колонка)
        return '';
    }
    /**
     * Нормалізує ID до числа, якщо можливо, або повертає рядок/порожнє значення
     */
    normalizeId(id) {
        if (id === null || id === undefined) return '';
        const num = Number(id);
        if (!Number.isNaN(num) && Number.isFinite(num)) return num;
        return ('' + id);
    }

    /**
     * Відправляє подію select_item в dataLayer
     * @param {Object} productData - Дані товару
     */
    sendSelectItemEvent(productData) {
        console.log('🔍 Вихідні дані товару:', productData);
        
        if (!window.dataLayer) {
            window.dataLayer = [];
        }
        
        if (!Array.isArray(window.dataLayer)) {
            console.error('❌ window.dataLayer не є масивом!', window.dataLayer);
            return;
        }
        
        // Перевіряємо глобальне дублювання
        const productId = this.extractProductId(productData);
        const normalizedId = this.normalizeId(productId);
        const now = Date.now();
        const selectItemKey = `select_item_${normalizedId}`;
        const currentPage = window.location.pathname;
        
        // Очищаємо трекер при зміні сторінки
        if (this.globalTracker.currentPage !== currentPage) {
            this.globalTracker.lastSentAt.clear();
            this.globalTracker.currentPage = currentPage;
            console.log('select_item 🧹 Очищено трекер при зміні сторінки:', currentPage);
        }
        
        // Перевіряємо блокировку ПОСЛЕ очистки трекера
        const lastSentAt = this.globalTracker.lastSentAt.get(selectItemKey);
        
        console.log('select_item 🔍 Перевірка блокировки (зараз відключена):', {
            productId: normalizedId,
            lastSentAt: lastSentAt,
            timeDiff: lastSentAt ? now - lastSentAt : 'перший клік',
            willBlock: false // Блокування відключено для постійних кліків
        });
        
        // Не блокуємо дублювання - дозволяємо постійні кліки на img і h5
        // if (lastSentAt && now - lastSentAt < 10) {
        //     console.log('select_item 🚫 Блокуємо дублювання для товару:', productData.title, 'через', now - lastSentAt, 'мс');
        //     return;
        // } 
        
        // Очищуємо попередні дані ecommerce
        window.dataLayer.push({ecommerce: null});
        
        // Форматуємо дані товару
        const formattedProduct = this.formatProductData(productData);
        
        console.log('📊 Форматовані дані товару для GA:', formattedProduct);
        
        // Відправляємо подію
        const eventData = {
            event: 'select_item',
            ecommerce: {
                items: [formattedProduct]
            }
        };
        
        // Додаємо контекст списку
        if (this.listContext) {
            eventData.item_list_name = this.listContext.name;
            eventData.item_list_id = this.listContext.id;
        }
        
        console.log('📤 Дані для dataLayer:', eventData);
        window.dataLayer.push(eventData);
        console.log('✅ Подія відправлена в dataLayer');
        
        // Принудительно відправляємо подію
        if (window.gtag) {
            const gtagData = {
                event_category: 'ecommerce',
                event_label: productData.title,
                value: formattedProduct.price
            };
            console.log('📤 Дані для gtag:', gtagData);
            window.gtag('event', 'select_item', gtagData);
            console.log('✅ Подія відправлена через gtag');
        } else {
            console.warn('⚠️ window.gtag не знайдено');
        }
        
        // Оновлюємо глобальний реєстр
        this.globalTracker.lastSentAt.set(selectItemKey, now);
        
        // Зберігаємо дані в sessionStorage
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                if (formattedProduct.item_category) {
                    sessionStorage.setItem('ga_last_item_category', formattedProduct.item_category);
                }
                if (this.listContext?.name) {
                    sessionStorage.setItem('ga_last_item_list_name', this.listContext.name);
                }
                if (this.listContext?.id) {
                    sessionStorage.setItem('ga_last_item_list_id', String(this.listContext.id));
                }
                if (formattedProduct.price) {
                    sessionStorage.setItem(`ga_last_item_price_${normalizedId}`, String(formattedProduct.price));
                }
            }
        } catch (e) {
            // no-op: безпечне ігнорування помилок доступу до storage
        }
        
        console.log('select_item ✅ Відправлено select_item:', productData.title);
    }
    /**
     * Обробляє клік по конкретному товару
     * @param {Object} productData - Дані товару
     */
    handleProductClick(productData) {
        const productId = this.extractProductId(productData);
        console.log('🎯 handleProductClick в трекері викликано для товару:', productData?.title, productId);
        this.sendSelectItemEvent(productData);
    }
    /**
     * Очищає відстеження для нової сторінки
     */
    reset() {
        this.listContext = null;
        this.clearGlobalTracker();
        this.globalTracker.currentPage = null;
    }
    
    /**
     * Очищає глобальний трекер
     */
    clearGlobalTracker() {
        if (window.__globalClickTracker) {
            window.__globalClickTracker.lastSentAt.clear();
        }
    }
    
    /**
     * Очищає стан конкретного товару
     */
    clearProductState(productId) {
        if (window.__globalClickTracker) {
            const normalizedId = this.normalizeId(productId);
            const selectItemKey = `select_item_${normalizedId}`;
            window.__globalClickTracker.lastSentAt.delete(selectItemKey);
        }
    }
    
    /**
     * Отримує стан глобального трекера
     */
    getGlobalTrackerState() {
        if (window.__globalClickTracker) {
            return {
                lastSentAt: Object.fromEntries(window.__globalClickTracker.lastSentAt)
            };
        }
        return null;
    }
}


// Експортуємо для використання
export default ProductClickTracker;
